import React, { useState } from 'react';
import {
  App,
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, ShopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { useAppSelector } from '../hooks/redux';
import { isAdmin, isSupportUser } from '../utils/roleHelpers';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import {
  Restaurant,
  useCreateRestaurantMutation,
  useDeleteRestaurantMutation,
  useGetRestaurantsQuery,
} from '../services/restaurantsApi';

const { Title, Text } = Typography;

/**
 * Gestión de sucursales de la organización (multi-restaurante). Cada sucursal paga
 * el precio completo del plan de la org: agregar suma +1 a la cantidad del plan en
 * la suscripción (prorrateado); cancelar la resta y desactiva la sucursal. La
 * incluida en el plan no se puede cancelar.
 */
const SucursalesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { message } = App.useApp();
  const { user } = useAppSelector((state) => state.auth);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const userIsAdmin = isAdmin(user?.role) || isSupportUser(user?.email);

  const { data: restaurants = [], isLoading } = useGetRestaurantsQuery();
  const [createRestaurant, { isLoading: creating }] = useCreateRestaurantMutation();
  const [deleteRestaurant] = useDeleteRestaurantMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await createRestaurant(values).unwrap();
      message.success('Sucursal creada. El cargo se prorratea en tu suscripción.');
      setIsModalOpen(false);
      form.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return; // validación del form
      message.error(error?.data?.message || 'No se pudo crear la sucursal', 6);
    }
  };

  const handleCancel = async (restaurant: Restaurant) => {
    try {
      await deleteRestaurant(restaurant.id).unwrap();
      message.success(`${restaurant.name} cancelada. El cobro se ajusta en Stripe.`);
    } catch (error: any) {
      message.error(error?.data?.message || 'No se pudo cancelar la sucursal', 6);
    }
  };

  const columns = [
    {
      title: 'Sucursal',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Restaurant) => (
        <Space>
          <Text strong>{name}</Text>
          {record.isFirstRestaurant && <Tag>Incluida en el plan</Tag>}
        </Space>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) =>
        isActive ? <Tag color="green">Activa</Tag> : <Tag>Cancelada</Tag>,
    },
    ...(isMobile
      ? []
      : [
          {
            title: 'Creada',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt: string) => formatInTimeZone(createdAt, 'DD/MM/YYYY'),
          },
        ]),
    {
      title: '',
      key: 'actions',
      render: (_: unknown, record: Restaurant) =>
        record.isActive && !record.isFirstRestaurant ? (
          <Popconfirm
            title={`¿Cancelar ${record.name}?`}
            description="Se deja de cobrar y sus usuarios pierden acceso."
            okText="Cancelar sucursal"
            okButtonProps={{ danger: true }}
            cancelText="Volver"
            onConfirm={() => handleCancel(record)}
          >
            <Button danger type="text" size="small">
              Cancelar
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  if (!userIsAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="warning" message="Solo el administrador puede gestionar sucursales." />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/configuracion')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Volver a Configuración
      </Button>

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <Title level={isMobile ? 3 : 2} style={{ marginBottom: 4 }}>
            <ShopOutlined /> Sucursales
          </Title>
          <Text type="secondary">
            Cada sucursal paga el precio de tu plan, prorrateado en la misma suscripción.
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
          Agregar sucursal
        </Button>
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={restaurants}
          columns={columns}
          pagination={false}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      <Modal
        title="Agregar sucursal"
        open={isModalOpen}
        onOk={handleCreate}
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={creating}
        okText="Crear y cobrar prorrateado"
        width={isMobile ? '90%' : 520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Nombre de la sucursal"
            rules={[{ required: true, message: 'Ingrese el nombre' }, { max: 200 }]}
          >
            <Input placeholder="Sucursal Centro" />
          </Form.Item>
          <Form.Item name="address" label="Dirección" rules={[{ max: 500 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Teléfono" rules={[{ max: 20 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email inválido' }, { max: 200 }]}>
            <Input type="email" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SucursalesPage;
